import { css } from '@emotion/react'
import { type CSSProperties } from 'react'
import {
  ColorPrimitives,
  CornerRadius,
  FontWeight,
  gemVar,
  LineWeight,
  Shadow,
  ShadowElevation,
  Size,
  Spacing,
  ThemedColors,
  ThemedFocusIndicator,
  Timing,
  Transition,
  Typography,
} from '@opallabs/gem'
import { modernWithFallback } from '@opallabs/lambda'
import { barColors } from '../resolveItemColor'
import { CalendarSizes } from '../sizes'

const rootClass = 'opal-calendar-panel'

export const selectors = {
  root: rootClass,
  back: `${rootClass}-back`,
  body: `${rootClass}-body`,
  crumbs: `${rootClass}-crumbs`,
  cycle: `${rootClass}-cycle`,
  dates: `${rootClass}-dates`,
  openPill: `${rootClass}-open-pill`,
  footer: `${rootClass}-footer`,
  header: `${rootClass}-header`,
  headerTop: `${rootClass}-header-top`,
  hero: `${rootClass}-hero`,
  itemBody: `${rootClass}-item-body`,
  heroButton: `${rootClass}-hero-button`,
  heroContent: `${rootClass}-hero-content`,
  heroImage: `${rootClass}-hero-image`,
  heroMoment: `${rootClass}-hero-moment`,
  itemTitle: `${rootClass}-item-title`,
  keyCatcher: `${rootClass}-key-catcher`,
  navigation: `${rootClass}-navigation`,
  momentBadge: `${rootClass}-moment-badge`,
  momentRow: `${rootClass}-moment-row`,
  momentThumb: `${rootClass}-moment-thumb`,
  momentThumbImage: `${rootClass}-moment-thumb-image`,
  momentThumbSurface: `${rootClass}-moment-thumb-surface`,
  panel: `${rootClass}-panel`,
  privateLock: `${rootClass}-private-lock`,
  row: `${rootClass}-row`,
  rowBody: `${rootClass}-row-body`,
  rowDetails: `${rootClass}-row-details`,
  rowLine: `${rootClass}-row-line`,
  rowMark: `${rootClass}-row-mark`,
  rowMeta: `${rootClass}-row-meta`,
  rowParent: `${rootClass}-row-parent`,
  rowRange: `${rootClass}-row-range`,
  rowStatus: `${rootClass}-row-status`,
  rowTime: `${rootClass}-row-time`,
  rowTitle: `${rootClass}-row-title`,
  stage: `${rootClass}-stage`,
  subheading: `${rootClass}-subheading`,
  summary: `${rootClass}-summary`,
  summaryChannel: `${rootClass}-summary-channel`,
  summaryCount: `${rootClass}-summary-count`,
  summaryHeadline: `${rootClass}-summary-headline`,
  summaryMore: `${rootClass}-summary-more`,
  tabPanel: `${rootClass}-tab-panel`,
  tabs: `${rootClass}-tabs`,
  title: `${rootClass}-title`,
  truncate: `${rootClass}-truncate`,
} as const

export const stateVariables = {
  multiDay: 'is-multi-day',
  open: 'is-open',
  placeholder: 'is-placeholder',
} as const

/**
 * A row's tint, set per row rather than per rule, because the colour belongs to
 * the item. Holding it in a variable is what lets the hover rule live in the
 * stylesheet with the rest of them: an inline style cannot answer `:hover`.
 */
export const cssVariables = {
  rowFill: '--opal-calendar-panel-row-fill',
  rowHoverFill: '--opal-calendar-panel-row-hover-fill',
} as const

/** Width of the panel when it is showing something. */
export const PANEL_INLINE_SIZE = 400

const truncate = {
  minInlineSize: 0,
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
} as const

const divider = `${LineWeight.Small}px solid ${gemVar(ThemedColors.StrokeSecondary1)}`

/** A moment's tile: the box the row and the panel draw a cover into. */
const TILE_SIZE = Spacing.XS * 9

/**
 * The shape of a tile: three rounded corners, and a bite out of the fourth for
 * the mark that says what kind of thing it is. The mark hangs a pixel over the
 * corner, so the bite is a pixel smaller than the mark's box, and its inner
 * corner is rounded the way the mark's own box is. Figma cuts the same shape
 * with a boolean; here it is a mask, so the row shows through around the mark
 * whatever the row's colour.
 */
const tileMask = (() => {
  const size = TILE_SIZE
  const corner = CornerRadius.Large
  const bite = Size.Icon - LineWeight.Small
  const biteCorner = CornerRadius.Small
  const path = [
    `M${bite} 0`,
    `H${size - corner}`,
    `A${corner} ${corner} 0 0 1 ${size} ${corner}`,
    `V${size - corner}`,
    `A${corner} ${corner} 0 0 1 ${size - corner} ${size}`,
    `H${corner}`,
    `A${corner} ${corner} 0 0 1 0 ${size - corner}`,
    `V${bite}`,
    `H${bite - biteCorner}`,
    `A${biteCorner} ${biteCorner} 0 0 0 ${bite} ${bite - biteCorner}`,
    `Z`,
  ].join('')
  const svg = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${size} ${size}"><path d="${path}"/></svg>`

  return `url("data:image/svg+xml,${encodeURIComponent(svg)}")`
})()

/**
 * What a moment shows where its cover picture would be: the darker of the two
 * sticky yellows a placeholder moment wears, on the tile, so it stands off the
 * lighter one on the row. Gem holds the light values; the dark ones are the same
 * hues held at the lightness a dark surface sits at.
 */
const emptyCoverFill = {
  backgroundColor: gemVar(ColorPrimitives.StickyDarkYellow),

  '[data-gem-theme="dark"] &': { backgroundColor: '#4C4629' },
} as const

/** The row a placeholder moment sits on: the lighter sticky yellow. */
const placeholderFill = {
  backgroundColor: gemVar(ColorPrimitives.StickyLightYellow),

  '[data-gem-theme="dark"] &': { backgroundColor: '#33301C' },
} as const

export const root = css({
  // The panel's own type and ink. The month view is mounted into a page whose
  // body text is not Gem's, so nothing in the panel may inherit from it.
  ...Typography.T500,
  backgroundColor: gemVar(ThemedColors.Surface1),
  blockSize: '100%',
  borderInlineStart: `${LineWeight.Small}px solid transparent`,
  boxSizing: 'border-box',
  color: gemVar(ThemedColors.TextPrimary),
  display: 'flex',
  // The same page turns text selection off for its whole layout
  // (`#moments .opal_ui.section.full`). The panel is for reading, so it turns
  // selection back on.
  userSelect: 'text',
  flex: 'none',
  inlineSize: 0,
  overflow: 'hidden',
  // The stages inside are placed against the root, so a panel on its way out
  // and the one on its way in can share the same ground.
  position: 'relative',
  ...Transition(['inlineSize'], {
    curve: 'ease',
    duration: Timing.Fast,
    motionProperties: ['inlineSize'],
  }),

  [`&.${stateVariables.open}`]: {
    borderInlineStartColor: gemVar(ThemedColors.StrokeSecondary1),
    inlineSize: PANEL_INLINE_SIZE,
  },

  // Exists only to catch Escape bubbling up from the panel, so it must not sit
  // between the root and the column in the layout.
  [`.${selectors.keyCatcher}`]: { display: 'contents' },

  // One stage per panel, filling the root. During a push two stages are on
  // show at once, one sliding out as the other slides in over the same ground.
  [`.${selectors.stage}`]: {
    display: 'flex',
    inset: 0,
    position: 'absolute',
  },

  // The column keeps its full width while the root collapses around it, so the
  // text inside does not reflow on the way out.
  [`.${selectors.panel}`]: {
    blockSize: '100%',
    boxSizing: 'border-box',
    display: 'flex',
    flexDirection: 'column',
    flex: 'none',
    inlineSize: PANEL_INLINE_SIZE,
  },

  // The heading and the summary are one block, held above the list rather than
  // scrolling with it: what the day holds should stay readable while a person
  // reads down what is in it.
  [`.${selectors.header}`]: {
    borderBlockEnd: divider,
    display: 'grid',
    flex: 'none',
    paddingBlock: Spacing.M,
    paddingInlineEnd: Spacing.XS * 3.5,
    paddingInlineStart: Spacing.M,
    rowGap: Spacing.M,
  },
  [`.${selectors.headerTop}`]: {
    alignItems: 'center',
    columnGap: Spacing.S,
    display: 'flex',
  },
  [`.${selectors.title}`]: {
    ...Typography.H300,
    ...truncate,
    color: gemVar(ThemedColors.TextPrimary),
    flex: 1,
  },

  [`.${selectors.body}`]: {
    flex: 1,
    paddingBlockEnd: Spacing.S,
    ...modernWithFallback(
      [{ overflowBlock: 'auto' }, { overflowY: 'auto' }],
      'overflowBlock: auto'
    ),
  },
  // The item panel's body. Everything in it is inset the same amount — the
  // title, the picture and the fields — so the rules under the fields line up
  // with the picture's edge. Only the content rows in a tab break out of it,
  // which `tabPanel` undoes. The last field has no rule under it, so the
  // padding below is all the room it gets.
  [`.${selectors.body}.${selectors.itemBody}`]: {
    paddingBlockEnd: Spacing.L,
    paddingBlockStart: Spacing.S,
    paddingInline: Spacing.L,
  },

  [`.${selectors.footer}`]: {
    borderBlockStart: divider,
    display: 'flex',
    flex: 'none',
    padding: Spacing.M,

    // Whatever the host draws runs the full width of the panel, so the way to
    // make something reads as the panel's own control and not as a button
    // left in a corner.
    '> *': { flex: 1 },
  },

  [`.${selectors.subheading}`]: {
    ...Typography.T300,
    borderBlockEnd: divider,
    color: gemVar(ThemedColors.TextSecondary),
    fontWeight: FontWeight.Semibold,
    padding: `${Spacing.XS * 5}px ${Spacing.XS * 3}px ${Spacing.S}px`,
  },

  // What the day holds, before the list of it. A quiet surface rather than a
  // card: it is a heading for the rows below, not an object of its own.
  [`.${selectors.summary}`]: {
    backgroundColor: gemVar(ThemedColors.Surface2),
    borderRadius: CornerRadius.Large,
    display: 'grid',
    padding: Spacing.S,
    rowGap: Spacing.XS,
  },
  [`.${selectors.summaryHeadline}`]: {
    ...Typography.H50,
    color: gemVar(ThemedColors.TextPrimary),
    marginBlockEnd: Spacing.XS,
  },
  // Label twins, trimmed to their ink, so a 12px line centres on the mark
  // beside it rather than sitting low under its own leading.
  [`.${selectors.summaryChannel}`]: {
    ...Typography.TL300,
    alignItems: 'center',
    color: gemVar(ThemedColors.TextPrimary),
    columnGap: Spacing.XS,
    display: 'flex',
    fontWeight: FontWeight.Medium,
  },
  [`.${selectors.summaryCount}`]: {
    color: gemVar(ThemedColors.TextPrimary),
    flex: 'none',
    // Pushed to the trailing edge so the counts line up down the block, however
    // long the channel names are.
    marginInlineStart: 'auto',
  },
  [`.${selectors.summaryMore}`]: {
    ...Typography.TL300,
    color: gemVar(ThemedColors.TextSecondary),
  },

  // A row runs the full width of the panel and is separated from the next by a
  // line, not by a card: the panel is a list of a day, not a set of objects.
  [`.${selectors.row}, .${selectors.momentRow}`]: {
    alignItems: 'start',
    background: 'none',
    border: 'none',
    borderBlockEnd: divider,
    columnGap: Spacing.S,
    cursor: 'pointer',
    display: 'flex',
    inlineSize: '100%',
    padding: Spacing.XS * 3,
    textAlign: 'start',

    '&:hover': { backgroundColor: gemVar(ThemedColors.Surface2) },
    '&:focus-visible': ThemedFocusIndicator,
  },
  // The color a person chose, on the leading edge of the row it belongs to. It
  // stands inside the padding rather than against the panel's edge, so a column
  // of rows reads as a list with a margin and not as a stack of tabs.
  //
  // An item with no colour keeps the swatch, faintly, so its name lines up
  // with the names around it and the row does not read as missing something.
  [`.${selectors.rowStatus}`]: {
    alignSelf: 'stretch',
    backgroundColor: `color-mix(in oklab, ${gemVar(ThemedColors.TextPrimary)} 10%, transparent)`,
    borderRadius: CornerRadius.XSmall,
    flex: 'none',
    inlineSize: Spacing.XS,
  },
  [`.${selectors.rowBody}`]: {
    display: 'flex',
    flex: 1,
    flexDirection: 'column',
    minInlineSize: 0,
    rowGap: Spacing.S,
  },
  // The lines under the name sit closer to each other than to the name, and
  // closer still on a row with one day to say. Nothing to say takes no room.
  //
  // Every line is the height of a channel mark, whatever it holds: a trimmed
  // date is shorter than that and the moment glyph's box is taller, and left
  // to their own heights the gaps between the lines came out uneven.
  [`.${selectors.rowDetails}`]: {
    alignItems: 'center',
    display: 'grid',
    gridAutoRows: CalendarSizes.cardDetailIconSize,
    rowGap: Spacing.XS,

    '&:empty': { display: 'none' },
  },
  [`.${selectors.row}.${stateVariables.multiDay} .${selectors.rowDetails}`]: {
    rowGap: Spacing.XS * 1.5,
  },
  [`.${selectors.rowLine}`]: {
    alignItems: 'center',
    columnGap: Spacing.XS / 2,
    display: 'flex',
  },
  // A moment leads with a tile rather than with its name, because a day panel
  // is read down the leading edge and that is where the moments are.
  [`.${selectors.momentRow}`]: {
    backgroundColor: `var(${cssVariables.rowFill}, transparent)`,
    // A tinted row needs a darker line than a plain one to show where it ends.
    borderBlockEndColor: gemVar(ThemedColors.StrokeSecondary2),

    '&:hover': {
      backgroundColor: `var(${cssVariables.rowHoverFill}, ${gemVar(ThemedColors.Surface2)})`,
    },
  },
  // A placeholder moment — one with no picture yet — is drawn the way the week
  // view draws it, on sticky yellow rather than in a colour of its own.
  [`.${selectors.momentRow}.${stateVariables.placeholder}`]: {
    ...placeholderFill,

    '&:hover': {
      backgroundColor: `color-mix(in oklab, ${gemVar(ColorPrimitives.StickyDarkYellow)} 40%, ${gemVar(ColorPrimitives.StickyLightYellow)})`,

      '[data-gem-theme="dark"] &': { backgroundColor: '#3D3921' },
    },
  },
  // The swatch on a moment row is the height of the tile beside it, not of the
  // row: the two read as one leading block, with the name hanging off them.
  [`.${selectors.momentRow} .${selectors.rowStatus}`]: {
    alignSelf: 'start',
    blockSize: Spacing.XS * 9,
  },
  [`.${selectors.momentThumb}`]: {
    blockSize: TILE_SIZE,
    display: 'inline-block',
    flex: 'none',
    inlineSize: TILE_SIZE,
    position: 'relative',
  },
  // The mask shapes the surface under the mark, not the mark itself: the mark
  // sits in the bite the mask takes out of the corner, on the row rather than
  // on a white square.
  [`.${selectors.momentThumbSurface}`]: {
    ...emptyCoverFill,
    blockSize: '100%',
    display: 'block',
    inlineSize: '100%',
    maskImage: tileMask,
    maskSize: '100% 100%',
  },
  [`.${selectors.momentThumbImage}`]: {
    blockSize: '100%',
    display: 'block',
    inlineSize: '100%',
    objectFit: 'cover',
  },
  // The mark is recognised the same way wherever a moment is drawn: on the
  // corner of its tile, and on the corner of its picture. On a tile it hangs a
  // pixel over the corner, in the bite the mask takes out for it.
  [`.${selectors.momentBadge}`]: {
    alignItems: 'center',
    color: gemVar(ThemedColors.TextPrimary),
    display: 'inline-flex',
    insetBlockStart: -LineWeight.Small,
    insetInlineStart: -LineWeight.Small,
    justifyContent: 'center',
    position: 'absolute',
  },
  // The marks stand a little further from a moment's name than from content's:
  // the name can run to two lines, and the marks sit level with the first.
  [`.${selectors.momentRow} .${selectors.rowBody}`]: {
    paddingInlineEnd: Spacing.S,
  },
  // A moment's name is the one thing on the row worth two lines of the panel.
  [`.${selectors.momentRow} .${selectors.rowTitle}`]: {
    display: '-webkit-box',
    overflow: 'hidden',
    whiteSpace: 'normal',
    WebkitBoxOrient: 'vertical',
    WebkitLineClamp: 2,
  },
  // Not truncated: a trimmed box ends at the cap height, so hiding its overflow
  // would clip the ascenders. Two short dates never run out of room anyway.
  [`.${selectors.rowRange}`]: {
    ...Typography.TL300,
    color: gemVar(ThemedColors.TextSecondary),
    fontWeight: FontWeight.Medium,
    whiteSpace: 'nowrap',
  },

  [`.${selectors.rowTitle}`]: {
    ...Typography.H50,
    ...truncate,
    color: gemVar(ThemedColors.TextPrimary),
    flex: 1,
  },
  // When it goes out, what state it is in, and who can see it — held together
  // at the trailing edge so they stay in one place from row to row.
  [`.${selectors.rowTime}`]: {
    ...Typography.T300,
    alignItems: 'center',
    color: gemVar(ThemedColors.TextSecondary),
    columnGap: Spacing.XS / 2,
    display: 'inline-flex',
    flex: 'none',
  },
  [`.${selectors.rowMeta}`]: {
    ...Typography.TL300,
    ...truncate,
    alignItems: 'center',
    color: gemVar(ThemedColors.TextSecondary),
    columnGap: Spacing.XS / 2,
    display: 'flex',
    flex: 'none',
    fontWeight: FontWeight.Medium,
  },
  // The moment a piece of content belongs to has a line to itself, under the
  // name, so it is never squeezed against the name it explains.
  [`.${selectors.rowParent}`]: {
    ...Typography.TL300,
    ...truncate,
    alignItems: 'center',
    color: gemVar(ThemedColors.TextSecondary),
    columnGap: Spacing.XS / 2,
    display: 'flex',
    flex: 'none',
    fontWeight: FontWeight.Medium,
  },
  // The moment glyph paints its 12px of ink in the middle of a 16px box. Pulled
  // in by the padding on each side, its ink starts where the channel mark's
  // does, and the name after it starts where the account does.
  [`.${selectors.rowMark}`]: {
    display: 'inline-flex',
    marginInline:
      -(CalendarSizes.cardMomentIconSize - CalendarSizes.cardDetailIconSize) /
      2,
  },

  // The day the item was opened from, and the way out. The day is a control
  // rather than a crumb trail: there is only ever one step back.
  [`.${selectors.crumbs}`]: {
    alignItems: 'center',
    display: 'flex',
    flex: 'none',
    justifyContent: 'space-between',
    paddingBlockStart: Spacing.L,
    // The buttons' edges sit on the body's edges, the way the title does.
    paddingInline: Spacing.L,
  },
  // The way back wears the same 4px on every side, as a lone icon does: a
  // small button's 8px beside text would push the chevron off the title's edge.
  [`.${selectors.back} button`]: {
    paddingInline: Spacing.XS,
  },
  [`.${selectors.cycle}`]: {
    alignItems: 'center',
    display: 'flex',
    justifyContent: 'space-between',
    marginBlockStart: Spacing.S,
  },
  // Stepping and opening are one control: three buttons in one rule, with no
  // rules of their own, so the group reads as a single thing to press.
  [`.${selectors.navigation}`]: {
    borderColor: gemVar(ThemedColors.StrokeSecondary2),
    borderRadius: CornerRadius.Small,
    borderStyle: 'solid',
    borderWidth: LineWeight.Small,
    display: 'inline-flex',
    overflow: 'hidden',

    button: {
      border: 'none',
      borderRadius: 0,
      maxBlockSize: Spacing.XS * 5.5,
      minBlockSize: Spacing.XS * 5.5,
      // A chevron's segment is 24px: the 16px mark and 4px either side.
      paddingInline: Spacing.XS,
    },
  },
  // Privacy is a lock at the trailing edge rather than a row of its own: it is
  // one bit, and a row would give it the weight of a relationship.
  [`.${selectors.privateLock}`]: {
    alignItems: 'center',
    color: gemVar(ThemedColors.IconSecondary),
    display: 'inline-flex',
    marginInlineStart: 'auto',
  },
  // Open sits between the chevrons, held in the pressed blue that says it is
  // the one thing in the group that leaves the panel.
  [`.${selectors.openPill}`]: {
    display: 'inline-flex',

    button: {
      backgroundColor: gemVar(ThemedColors.Primary3),
      color: gemVar(ThemedColors.TextLinks),
      // The design's 7px: its 8px segment less the border the group draws.
      paddingInline: Spacing.S - LineWeight.Small,

      '&:hover': { backgroundColor: gemVar(ThemedColors.Primary4) },
    },
  },

  [`.${selectors.itemTitle}`]: {
    ...Typography.H300,
    color: gemVar(ThemedColors.TextPrimary),
    // A heading's own margins would open the design's 8px gaps to 16px.
    margin: 0,
  },
  [`.${selectors.hero}`]: {
    display: 'flex',
    flex: 'none',
    flexDirection: 'column',
    marginBlockStart: Spacing.L,
    position: 'relative',
  },
  // A moment's cover fills a frame of a fixed shape, so a column of moments
  // does not jump about as their pictures change proportion.
  [`.${selectors.heroMoment}`]: {
    backgroundColor: gemVar(ThemedColors.Surface3),
    blockSize: Spacing.XL * 4 + Spacing.S,
    borderRadius: CornerRadius.Large,
    // The picture is square-cornered, so the hero has to be the thing that
    // rounds it.
    overflow: 'hidden',

    img: { blockSize: '100%', inlineSize: '100%', objectFit: 'cover' },
  },
  // Content is drawn whole, at whatever shape the channel publishes it in, held
  // on a surface that says the picture is a preview and not the item itself.
  [`.${selectors.heroContent}`]: {
    alignItems: 'center',
    backgroundColor: gemVar(ThemedColors.Surface2),
    borderRadius: CornerRadius.Small,
    justifyContent: 'center',
    padding: Spacing.M,

    // The picture is lifted a little off its surface, so it reads as a thing
    // laid on the panel and not as a region painted onto it.
    img: Shadow[ShadowElevation.E1],
  },
  [`.${selectors.heroButton}`]: {
    background: 'none',
    blockSize: '100%',
    border: 'none',
    cursor: 'pointer',
    display: 'flex',
    inlineSize: '100%',
    // A picture narrower than the panel sits in the middle of it, not against
    // its leading edge.
    justifyContent: 'center',
    padding: 0,

    '&:focus-visible': ThemedFocusIndicator,
  },
  [`.${selectors.heroImage}`]: {
    maxInlineSize: '100%',
  },
  // On the hero the mark stands away from the corner on a square of its own;
  // the picture is not masked for it there, so nothing shows through.
  [`.${selectors.hero} .${selectors.momentBadge}`]: {
    backgroundColor: gemVar(ThemedColors.Surface1),
    borderRadius: CornerRadius.Small,
    insetBlockStart: Spacing.S,
    insetInlineStart: Spacing.S,
    padding: LineWeight.Large,
  },

  // `SidebarTabs` draws the rule and the line under the chosen tab itself.
  [`.${selectors.tabs}`]: {
    marginBlockStart: Spacing.L,
  },
  // The rows inside a tab run the full width of the panel, so they break out of
  // the padding the rest of the panel's body carries.
  [`.${selectors.tabPanel}`]: {
    marginInline: -Spacing.L,
  },

  // The two dates share a row, because they are one fact read together.
  [`.${selectors.dates}`]: {
    columnGap: Spacing.XS * 3,
    display: 'flex',

    '> *': { flex: 1, minInlineSize: 0 },
  },
  // The last field needs no rule under it: the end of the list is the end of
  // the panel. Only the panel knows which field is last, so the panel says so,
  // as the block sidebar does for its own fields. The two dates share a row,
  // so the row is what has to be last, not the date on its trailing side.
  [`.${selectors.body} .lapidary-field:last-child:not(.${selectors.dates} > *)::after, .${selectors.body} .${selectors.dates}:last-child .lapidary-field::after`]:
    {
      display: 'none',
    },
  [`.${selectors.truncate}`]: truncate,
})

/**
 * Tints a whole row with a color a person chose, and says what the row does
 * under the pointer. Both are the grid's own bar colors, so a moment's row and
 * its bar are the same colour.
 */
export const rowFillStyle = (
  color: string | undefined
): CSSProperties | undefined => {
  const derived = color ? barColors(color) : null
  if (!derived) return undefined

  return {
    [cssVariables.rowFill]: derived.fill,
    [cssVariables.rowHoverFill]: derived.hoverFill,
  } as CSSProperties
}

/**
 * Paints a surface with the chosen color at full strength — the tiles and the
 * hero, which are big enough to carry it and read as the thing itself.
 */
export const solidStyle = (
  color: string | undefined
): CSSProperties | undefined => {
  if (!color) return undefined

  const derived = barColors(color)
  if (!derived) return undefined

  return { backgroundColor: color, color: derived.contrastForeground }
}
